import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import MarketSeriesChart from '../components/stock/MarketSeriesChart';
import { useAppSelector } from '../store/hooks';
import {
  addLiveSummaryHolding,
  searchLiveSummaryHoldings,
  type LiveSummaryHoldingMutationAction,
  type LiveSummaryHoldingMutationResult,
} from '../api/liveSummaryApi';
import { trackFunnelLandingView, trackUxEvent } from '../analytics/analytics';
import { emitTrackedReturnMilestones, recordTrackedActivation } from '../analytics/retentionMilestones';
import {
  ensureTrackedStockWebPush,
  requestTrackingWebPushPermission,
  type TrackedStockWebPushStatus,
} from '../components/live/webPushClient';
import {
  clearStockTrackPending,
  readStockTrackPending,
  saveStockTrackPending,
} from '../shared/stockTrackPending';
import './stockSeoPage.css';

type ValueLayer = { state?: string; label?: string; reasons?: string[] };
type BusinessEvidence = {
  ready?: boolean; stale?: boolean; needsReview?: boolean; financialIndustry?: boolean;
  state?: string; summary?: string; businessDate?: string; reportName?: string;
  industry?: string; subIndustry?: string; reportShape?: string; semanticStatus?: string;
  semanticConfidence?: string; profileReadinessScore?: number | null; consistencyIssueCount?: number | null;
};
type VerifiedValueState = { available?: boolean; state?: string; label?: string; tone?: string; verifiedAt?: string; basisDate?: string; source?: string; reportName?: string };
type NewEvidenceStatus = { state?: string; label?: string; summary?: string; conflict?: boolean };
type ValueJudgment = {
  contractVersion?: string; valueState?: string; valueStateLabel?: string; valueStateTone?: string;
  confidence?: string; confidenceLabel?: string; summary?: string; reasons?: string[]; nextChecks?: string[];
  recentValueChanges?: string[]; recentMarketChanges?: string[]; recentEvidenceChanges?: string[];
  businessEvidence?: BusinessEvidence; fundamental?: ValueLayer; thesis?: ValueLayer; marketDelivery?: ValueLayer;
  lastVerifiedState?: VerifiedValueState; lastVerifiedValueState?: VerifiedValueState;
  lastVerifiedValueStateCode?: string; lastVerifiedValueStateLabel?: string; lastVerifiedAt?: string; lastVerifiedSource?: string;
  newEvidenceStatus?: NewEvidenceStatus; newEvidence?: NewEvidenceStatus; newEvidenceState?: string; newEvidenceLabel?: string; newEvidenceSummary?: string; newEvidenceConflict?: boolean;
  dataQuality?: Record<string, unknown>;
};
type ConfirmedSnapshot = {
  tradeDate?: string; closePrice?: number | null; priceChangePct?: number | null; tradingValue?: number | null;
  foreignValueNet?: number | null; institutionValueNet?: number | null;
};

function cleanText(v: unknown): string { return String(v ?? '').replace(/\s+/g, ' ').trim(); }
function num(v: unknown): number | null { if (v === null || v === undefined || v === '') return null; const n = Number(v); return Number.isFinite(n) ? n : null; }
function fmtPrice(v: number | null): string { return v === null ? '—' : `${new Intl.NumberFormat('ko-KR').format(Math.round(v))}원`; }
function fmtPct(v: number | null): string { return v === null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(2)}%`; }
function fmtMoney(v: number | null): string { if (v === null) return '—'; const a = Math.abs(v), s = v < 0 ? '-' : ''; if (a >= 1e12) return `${s}${(a / 1e12).toFixed(2)}조`; if (a >= 1e8) return `${s}${(a / 1e8).toFixed(1)}억`; if (a >= 1e4) return `${s}${(a / 1e4).toFixed(1)}만`; return `${s}${new Intl.NumberFormat('ko-KR').format(Math.round(a))}`; }

function koreanTradeDateLabel(value: unknown, suffix = '종가 기준'): string {
  const text = cleanText(value);
  const match = text.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '가격 기준일 확인 중';
  return `${Number(match[2])}월 ${Number(match[3])}일 ${suffix}`;
}

function stringList(v: unknown): string[] { return Array.isArray(v) ? v.map(cleanText).filter(Boolean) : []; }
function stateClass(v?: string) { const x = cleanText(v).toUpperCase(); if (x === 'DAMAGED') return 'is-down'; if (x === 'SHAKEN') return 'is-warn'; if (x === 'PRESERVED' || x === 'STRENGTHENED') return 'is-good'; return 'is-neutral'; }
function deliveryClass(v?: string) { const x = cleanText(v).toUpperCase(); if (x === 'FAVORABLE') return 'is-good'; if (x === 'SUPPRESSED' || x === 'UNFAVORABLE') return 'is-warn'; return 'is-neutral'; }

function isResolvableLimitedJudgment(judgment: ValueJudgment | null): boolean {
  if (cleanText(judgment?.valueState).toUpperCase() !== 'LIMITED') return false;
  const business = judgment?.businessEvidence;
  const semanticState = [business?.semanticStatus, business?.state].map(cleanText).join(' ').toUpperCase();
  return Boolean(
    business?.needsReview
      || business?.stale
      || /PENDING|REVIEW|VERIFY|VALIDAT|PROCESS|REFRESH|검증|재검증|처리\s*중|갱신\s*중/.test(semanticState),
  );
}

function verifiedStateView(judgment: ValueJudgment | null): { available: boolean; state: string; label: string; verifiedAt: string; source: string } {
  const raw = judgment?.lastVerifiedState ?? judgment?.lastVerifiedValueState ?? {};
  const state = cleanText(raw.state ?? judgment?.lastVerifiedValueStateCode).toUpperCase();
  const label = cleanText(raw.label ?? judgment?.lastVerifiedValueStateLabel);
  return {
    available: Boolean(raw.available ?? Boolean(state || label)),
    state,
    label: label || state,
    verifiedAt: cleanText(raw.verifiedAt ?? raw.basisDate ?? judgment?.lastVerifiedAt),
    source: cleanText(raw.source ?? raw.reportName ?? judgment?.lastVerifiedSource),
  };
}

function newEvidenceView(judgment: ValueJudgment | null): { state: string; label: string; summary: string; conflict: boolean } {
  const raw = judgment?.newEvidenceStatus ?? judgment?.newEvidence ?? {};
  const state = cleanText(raw.state ?? judgment?.newEvidenceState).toUpperCase() || 'UNKNOWN';
  return {
    state,
    label: cleanText(raw.label ?? judgment?.newEvidenceLabel) || (state === 'UNKNOWN' ? '신규 근거 확인 중' : state),
    summary: cleanText(raw.summary ?? judgment?.newEvidenceSummary),
    conflict: Boolean(raw.conflict ?? judgment?.newEvidenceConflict),
  };
}

function isGoogleOrganicReferrer(): boolean {
  if (typeof document === 'undefined' || !document.referrer) return false;
  try {
    const host = new URL(document.referrer).hostname.toLowerCase();
    return host === 'google.com' || host === 'www.google.com' || host.startsWith('google.') || host.includes('.google.');
  } catch {
    return false;
  }
}

function trackingCtaLabel(judgment: ValueJudgment | null): string {
  if (isResolvableLimitedJudgment(judgment)) return '검증 결과 알려받기';
  if (cleanText(judgment?.valueState).toUpperCase() === 'LIMITED') return '이 종목 계속 감시하기';
  return '판단이 바뀌는 순간 알려받기';
}

function trackingCtaSupport(judgment: ValueJudgment | null, authenticated: boolean): string {
  if (authenticated) return '현재 전체 감시 기능 체험 중 · 변화 이력·사업 Event·알림까지 이어서 확인합니다.';
  if (isResolvableLimitedJudgment(judgment)) return '최신 근거 검증이 끝나거나 기존 투자 논리와 충돌하면 이어서 확인할 수 있습니다.';
  return '현재 판단은 무료입니다. 이후 중요한 변화부터 계정에 연결해 계속 감시합니다.';
}

function createTrackIntentId(ticker: string): string {
  try {
    if (typeof globalThis.crypto?.randomUUID === 'function') {
      return `stock-track:${ticker}:${globalThis.crypto.randomUUID()}`;
    }
  } catch {
    // 제한된 브라우저에서는 아래 fallback을 사용한다.
  }
  return `stock-track:${ticker}:${Date.now().toString(36)}:${Math.random().toString(36).slice(2, 10)}`;
}

function mutationEventName(action: LiveSummaryHoldingMutationAction): string {
  if (action === 'CREATED') return 'stock_track_created';
  if (action === 'RESTORED') return 'stock_track_restored';
  return 'stock_track_already_active';
}

function isTrackStateActive(value: string): boolean {
  const state = cleanText(value);
  return /^(감시 시작됨|이미 감시 중|감시 중|추적 중)/.test(state);
}

function trackStateForMutation(
  action: LiveSummaryHoldingMutationAction,
  pushStatus: TrackedStockWebPushStatus,
): string {
  const suffix = trackStateForPush(pushStatus).replace(/^추적 중 · /, '');
  if (action === 'ALREADY_ACTIVE') return `이미 감시 중 · ${suffix}`;
  return `감시 시작됨 · ${suffix}`;
}

type ConversionCtaProps = {
  ticker: string;
  label: string;
  support: string;
  authenticated: boolean;
  placement: 'bottom';
  disabled?: boolean;
  onClick: () => void;
};

function ConversionCta({ ticker, label, support, authenticated, placement, disabled = false, onClick }: ConversionCtaProps) {
  const rootRef = useRef<HTMLElement | null>(null);
  const trackedRef = useRef(false);

  useEffect(() => {
    const node = rootRef.current;
    if (!node || trackedRef.current) return;
    const emit = () => {
      if (trackedRef.current) return;
      trackedRef.current = true;
      trackUxEvent('track_cta_view', { ticker, stock_code: ticker, source: 'stock-seo', placement, authenticated });
    };
    if (typeof IntersectionObserver === 'undefined') {
      emit();
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.35)) {
        emit();
        observer.disconnect();
      }
    }, { threshold: [0.35] });
    observer.observe(node);
    return () => observer.disconnect();
  }, [authenticated, placement, ticker]);

  return (
    <section ref={rootRef} className="stock-value-continuity-cta">
      <div>
        <small>NEXT MONITORING</small>
        <h2>현재 근거를 확인하고,<br />이후 변화도 이어서 살펴보세요.</h2>
        <p>{support}</p>
      </div>
      <button type="button" onClick={onClick} disabled={disabled}>{label}</button>
    </section>
  );
}

function trackStateForPush(status: TrackedStockWebPushStatus): string {
  if (status === 'enabled' || status === 'already_enabled') return '추적 중 · 웹 알림 ON';
  if (status === 'permission_denied') return '추적 중 · 웹 알림 차단';
  if (status === 'permission_required') return '추적 중 · 웹 알림 권한 필요';
  if (status === 'unsupported') return '추적 중 · 웹 알림 미지원';
  return '추적 중 · 웹 알림 연결 확인 필요';
}

async function requestTrackPushPermission(ticker: string, source: string): Promise<void> {
  const shouldPrompt = typeof Notification !== 'undefined' && Notification.permission === 'default';
  if (shouldPrompt) {
    trackUxEvent('push_prompt_view', {
      ticker,
      stock_code: ticker,
      source,
      activation: 'TRACK_AUTO',
    });
  }

  try {
    await requestTrackingWebPushPermission();
  } catch {
    // 브라우저 권한/지원 문제로 관심종목 등록 자체가 실패하면 안 된다.
  }
}

async function activateTrackWebPush(
  ticker: string,
  source: string,
): Promise<TrackedStockWebPushStatus> {
  try {
    return await ensureTrackedStockWebPush();
  } catch {
    trackUxEvent('push_auto_activation_failed', {
      ticker,
      stock_code: ticker,
      source,
      activation: 'TRACK_AUTO',
    });
    return 'server_unavailable';
  }
}

export default function StockSeoPage() {
  const { ticker: rawTicker } = useParams<{ ticker: string }>();
  const ticker = cleanText(rawTicker);
  const valid = /^\d{6}$/.test(ticker);
  const auth = useAppSelector((x: any) => x.auth);
  const authenticated = Boolean(auth?.authInitialized && auth?.authStatus === 'AUTHENTICATED' && auth?.isAuthenticated && auth?.user?.email);
  const retentionIdentity = cleanText(auth?.user?.email);
  const [payload, setPayload] = useState<any>(null);
  const [loading, setLoading] = useState(valid);
  const [error, setError] = useState('');
  const [evidenceReloadKey, setEvidenceReloadKey] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [trackState, setTrackState] = useState('');
  const [trackSaving, setTrackSaving] = useState(false);
  // Guest-only explicit reveal: the expensive daily chart is not mounted until requested.
  const [guestChartTicker, setGuestChartTicker] = useState('');
  const guestChartOpen = !authenticated && guestChartTicker === ticker && valid;
  const guestChartSectionRef = useRef<HTMLElement | null>(null);
  const trackMutationInFlightRef = useRef('');
  const funnelViewRef = useRef<Set<string>>(new Set());
  const priceIntentRef = useRef<HTMLDivElement | null>(null);
  const valueJudgmentRef = useRef<HTMLDivElement | null>(null);
  const reasonCardRef = useRef<HTMLElement | null>(null);
  const changeCardRef = useRef<HTMLElement | null>(null);
  const topTrackCtaRef = useRef<HTMLButtonElement | null>(null);
  const conditionCardRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!valid) return;
    const device = window.matchMedia('(max-width:768px)').matches ? 'mobile' : 'desktop';
    const googleOrganic = isGoogleOrganicReferrer();
    trackFunnelLandingView({ source: 'stock-seo', stock_code: ticker, device });
    if (googleOrganic) {
      trackUxEvent('organic_landing', {
        ticker,
        stock_code: ticker,
        source: 'google-organic-stock',
        device,
        funnel_version: 'organic-v2',
      });
    }
  }, [ticker, valid]);


  useEffect(() => {
    if (!authenticated) return;
    emitTrackedReturnMilestones('stock-seo', retentionIdentity);
  }, [authenticated, retentionIdentity]);

  useEffect(() => {
    setPayload(null);
    setNotFound(false);

    if (!valid) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    let disposed = false;
    let timedOut = false;
    const startedAt = performance.now();
    const timer = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 12_000);
    setLoading(true);
    setError('');

    // API completion is separate from viewport impressions. Never label a price
    // impression as successful judgment loading. Emit no raw provider/PII data.
    const recordLoadResult = (result: string, httpStatus: number | null = null, data: any = null) => {
      if (disposed) return;
      trackUxEvent('stock_seo_data_result', {
        ticker,
        stock_code: ticker,
        source: 'stock-seo',
        result,
        http_status: httpStatus,
        has_judgment: Boolean(data?.valueJudgment && typeof data.valueJudgment === 'object'),
        price_available: num(data?.confirmedSnapshot?.closePrice) !== null,
        elapsedMs: Math.max(0, Math.round(performance.now() - startedAt)),
        funnel_version: 'organic-v4',
      });
    };

    void (async () => {
      try {
        const response = await fetch(`/api/main/market-intelligence/stocks/${ticker}`, {
          credentials: 'include',
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        if (disposed) return;
        if (response.status === 404 || response.status === 410) {
          setNotFound(true);
          recordLoadResult('NOT_FOUND', response.status);
          return;
        }
        if (!response.ok) {
          setError('가치 판단 조회에 실패했습니다. 잠시 후 다시 조회해 주세요.');
          recordLoadResult('HTTP_ERROR', response.status);
          return;
        }
        let value: any;
        try {
          value = await response.json();
        } catch {
          if (!disposed) {
            setError(timedOut
              ? '판단 데이터 응답이 지연되고 있습니다. 잠시 후 다시 조회해 주세요.'
              : '서버 응답을 해석하지 못했습니다. 잠시 후 다시 조회해 주세요.');
            recordLoadResult(timedOut ? 'TIMEOUT' : 'INVALID_RESPONSE', response.status);
          }
          return;
        }
        if (disposed) return;
        if (!value || typeof value !== 'object' || Array.isArray(value)) {
          setError('서버 응답 형식을 확인할 수 없습니다. 잠시 후 다시 조회해 주세요.');
          recordLoadResult('INVALID_RESPONSE', response.status);
          return;
        }
        setPayload(value);
        recordLoadResult(value.valueJudgment && typeof value.valueJudgment === 'object'
          ? 'READY' : 'NO_JUDGMENT', response.status, value);
      } catch (cause) {
        if (disposed) return;
        setError(timedOut
          ? '판단 데이터 응답이 지연되고 있습니다. 잠시 후 다시 조회해 주세요.'
          : '종목 가치 판단 데이터를 일시적으로 불러오지 못했습니다. 잠시 후 다시 조회해 주세요.');
        recordLoadResult(timedOut ? 'TIMEOUT' : 'NETWORK_ERROR');
      } finally {
        window.clearTimeout(timer);
        if (!disposed) setLoading(false);
      }
    })();
    return () => {
      disposed = true;
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [ticker, valid, evidenceReloadKey]);

  useEffect(() => {
    setGuestChartTicker('');
  }, [ticker]);

  useEffect(() => {
    if (!guestChartOpen) return;
    // Only runs after an explicit guest click, never when the page first loads.
    guestChartSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [guestChartOpen]);

  useEffect(() => {
    setTrackState('');
    setTrackSaving(false);
    trackMutationInFlightRef.current = '';
  }, [ticker]);

  useEffect(() => {
    if (!authenticated || !valid) return;

    let cancelled = false;
    void searchLiveSummaryHoldings(ticker, {limit: 5, timeoutMs: 5_000})
      .then((rows) => {
        if (cancelled) return;
        const current = rows.find((row) => row.ticker === ticker);
        if (current?.alreadyAdded) {
          setTrackState((previous) => previous || '이미 감시 중');
        }
      })
      .catch(() => {
        // 상태 동기화 실패는 실제 Track mutation을 막지 않는다.
      });

    return () => {
      cancelled = true;
    };
  }, [authenticated, ticker, valid]);

  useEffect(() => {
    if (!authenticated || !valid) return;

    const pending = readStockTrackPending();
    if (!pending || pending.type !== 'WATCHLIST' || pending.ticker !== ticker) return;

    if (trackMutationInFlightRef.current === ticker) return;
    trackMutationInFlightRef.current = ticker;
    setTrackSaving(true);

    trackUxEvent('login_after_value_view', {
      ticker,
      stock_code: ticker,
      source: 'stock-seo',
      pending_source: pending.source ?? null,
      intent_id: pending.intentId ?? null,
      funnel_version: 'organic-v3',
    });

    void (async () => {
      let mutation: LiveSummaryHoldingMutationResult;
      try {
        mutation = await addLiveSummaryHolding(ticker);
      } catch (cause: any) {
        // 순차 배포 중 구버전 서버가 중복을 409로 응답하는 경우도 no-op으로 수용한다.
        if (Number(cause?.response?.status) === 409) {
          mutation = {
            ticker,
            active: true,
            added: false,
            action: 'ALREADY_ACTIVE',
          };
        } else {
          setTrackState('추적 저장 확인 필요');
          return;
        }
      }

      clearStockTrackPending();
      const pushStatus = await activateTrackWebPush(ticker, 'stock-seo-post-login');
      const stateChanged = mutation.action === 'CREATED' || mutation.action === 'RESTORED';
      if (stateChanged) {
        recordTrackedActivation(ticker, retentionIdentity);
      }
      setTrackState(trackStateForMutation(mutation.action, pushStatus));
      trackUxEvent(mutationEventName(mutation.action), {
        ticker,
        stock_code: ticker,
        source: 'stock-seo-post-login',
        pending_source: pending.source ?? null,
        intent_id: pending.intentId ?? null,
        mutation_action: mutation.action,
        state_changed: stateChanged,
        push_status: pushStatus,
        funnel_version: 'organic-v3',
      });
    })().finally(() => {
      if (trackMutationInFlightRef.current === ticker) {
        trackMutationInFlightRef.current = '';
      }
      setTrackSaving(false);
    });
  }, [authenticated, retentionIdentity, ticker, valid]);

  const stock = payload?.stock ?? {};
  const name = cleanText(stock?.corpName ?? stock?.corp_name) || ticker;
  const market = cleanText(stock?.market);
  const judgment = (payload?.valueJudgment ?? null) as ValueJudgment | null;
  const snap = (payload?.confirmedSnapshot ?? null) as ConfirmedSnapshot | null;
  const business = judgment?.businessEvidence ?? {};
  const close = num(snap?.closePrice), rate = num(snap?.priceChangePct), foreign = num(snap?.foreignValueNet), institution = num(snap?.institutionValueNet), tradingValue = num(snap?.tradingValue);
  const smart = foreign === null && institution === null ? null : (foreign ?? 0) + (institution ?? 0);
  const valueChanges = stringList(judgment?.recentValueChanges);
  const marketChanges = stringList(judgment?.recentMarketChanges);
  const reasons = stringList(judgment?.reasons);
  const checks = stringList(judgment?.nextChecks);
  const disclosures = Array.isArray(stock?.disclosures) ? stock.disclosures : [];
  const latestDisclosure = disclosures[0] ?? null;
  const latestDisclosureTitle = cleanText(latestDisclosure?.reportName ?? latestDisclosure?.report_name ?? latestDisclosure?.title);
  const latestDisclosureDate = cleanText(latestDisclosure?.receiptDate ?? latestDisclosure?.receipt_date ?? latestDisclosure?.date);
  const issueDataStatus = stock?.issueDataStatus ?? {};
  const issueStatus = cleanText(issueDataStatus?.status).toUpperCase();
  const issueStatusLabel = latestDisclosureTitle
    ? '1건 공개'
    : issueStatus === 'PROCESSING'
      ? '처리 중'
      : issueStatus === 'NO_QUALIFYING_ISSUE'
        ? '중요 변화 없음'
        : issueStatus === 'NO_RECENT_DISCLOSURE_OBSERVED'
          ? '최근 관측 없음'
          : issueStatus === 'UNAVAILABLE'
            ? '상태 조회 실패'
            : '확인 필요';
  const issueEmptyText = issueStatus === 'PROCESSING'
    ? '최근 공시는 관측됐지만 영향 분석 처리가 아직 끝나지 않았습니다.'
    : issueStatus === 'NO_QUALIFYING_ISSUE'
      ? '최근 공시는 확인됐지만 현재 중요 변화로 분류된 항목은 없습니다.'
      : issueStatus === 'NO_RECENT_DISCLOSURE_OBSERVED'
        ? '최근 90일 동안 중요 공시로 관측된 항목이 없습니다. 수집 완료 여부와는 구분해 표시합니다.'
        : issueStatus === 'UNAVAILABLE'
          ? '공시 수집 상태 authority를 조회하지 못해 최근 이슈 여부를 확정하지 않습니다.'
          : '종목별 수집 완료 authority가 없어 최근 이슈 없음으로 단정하지 않습니다.';
  const evidenceChanges = stringList(judgment?.recentEvidenceChanges);
  const disclosureImpactLabel = latestDisclosureTitle
    ? evidenceChanges[0]
      || newEvidenceView(judgment).summary
      || (issueStatus === 'PROCESSING'
        ? '판단 영향 분석 중'
        : issueStatus === 'NO_QUALIFYING_ISSUE'
          ? '현재 중요 변화로 분류되지 않음'
          : '현재 판단 영향 확인 중')
    : '';
  const financialReasons = stringList(judgment?.fundamental?.reasons);
    const dataQuality = judgment?.dataQuality ?? {};
  const financialReady = dataQuality?.fundamentalReady === true
    && Number(dataQuality?.fundamentalMetricCount ?? 0) > 0
    && dataQuality?.fundamentalSnapshotMissing !== true
    && dataQuality?.fundamentalSnapshotStale !== true;
  const financialBasisDate = cleanText(dataQuality?.fundamentalBasisDate);
  const evidenceRefreshPending = !financialReady;
  const visibleDisclosures = authenticated ? disclosures.slice(0, 4) : disclosures.slice(0, 1);
  const visibleValueChanges = authenticated ? valueChanges.slice(0, 3) : valueChanges.slice(0, 1);
  const visibleMarketChanges = authenticated ? marketChanges.slice(0, 3) : marketChanges.slice(0, 1);
  const visibleChecks = checks.slice(0, 3);
  const trackLabel = authenticated ? trackingCtaLabel(judgment) : '이 종목 변화 추적';
  const trackSupport = authenticated
    ? trackingCtaSupport(judgment, true)
    : '종목을 등록하면 이후 변화 이력을 이어서 확인할 수 있습니다. 웹 알림은 별도 권한과 전송 상태에 따라 달라집니다.';
  const trackingActive = isTrackStateActive(trackState);
  const trackButtonLabel = trackSaving ? '저장 중...' : (trackState || trackLabel);
  const trackButtonDisabled = trackSaving || trackingActive;
  const businessStatus = business.ready && !business.needsReview && !business.stale
    ? '검증 완료'
    : business.needsReview || business.stale
      ? '최신 사업근거 검증 중'
      : '사업 근거 확인 필요';
  const verifiedState = verifiedStateView(judgment);
  const newEvidence = newEvidenceView(judgment);
  const limitedWithVerifiedBaseline = cleanText(judgment?.valueState).toUpperCase() === 'LIMITED' && verifiedState.available;
  const limitedState = cleanText(judgment?.valueState).toUpperCase() === 'LIMITED';
  const displayValueLabel = limitedState
    ? (limitedWithVerifiedBaseline ? '검증 중' : '확인 필요')
    : (judgment?.valueStateLabel || '확인 필요');
  const displayValueHeadline = limitedState
    ? limitedWithVerifiedBaseline && verifiedState.label
      ? newEvidence.conflict
        ? `기존 확인: ${verifiedState.label} · 재검토 중`
        : `기존 확인: ${verifiedState.label} · 최신 근거 재확인 중`
      : '확인 필요'
    : displayValueLabel;
  // P2: facts originate only from already-exposed verified/dated fields.
  // A disclosed event is not automatically a verified cause; never convert it into a causal claim.
  const observedEvidence: Array<{ label: string; value: string; note: string }> = [];
  if (business.ready === true && business.stale !== true && business.needsReview !== true) {
    observedEvidence.push({
      label: '사업 근거', value: '검증 완료',
      note: [cleanText(business.reportName), cleanText(business.businessDate)].filter(Boolean).join(' · ') || '검증 시점 표기 없음',
    });
  }
  if (financialReady && financialReasons.length > 0) {
    observedEvidence.push({
      label: '검증된 재무 설명', value: financialReasons[0],
      note: financialBasisDate ? `재무 기준일 ${financialBasisDate}` : '재무 기준일 미표기',
    });
  }
  if (cleanText(snap?.tradeDate) && (foreign !== null || institution !== null)) {
    observedEvidence.push({
      label: '수급 관측', value: `외국인 ${fmtMoney(foreign)} · 기관 ${fmtMoney(institution)}`,
      note: `거래일 ${cleanText(snap?.tradeDate)} · 순매수 금액 (원천 집계 기준)`,
    });
  }
  const visibleObservedEvidence = observedEvidence.slice(0, 3);
  const priceBasisLabel = snap?.tradeDate ? koreanTradeDateLabel(snap.tradeDate) : '가격 기준일 확인 중';

  const toggleGuestChart = () => {
    if (!valid || authenticated) return;
    const opening = !guestChartOpen;
    trackUxEvent('stock_seo_chart_toggle', {
      ticker,
      stock_code: ticker,
      source: 'stock-seo',
      action: opening ? 'OPEN' : 'CLOSE',
      funnel_version: 'organic-v5',
    });
    setGuestChartTicker(opening ? ticker : '');
  };

  const retryStockEvidence = () => {
    trackUxEvent('stock_seo_retry_click', {
      ticker, stock_code: ticker, source: 'stock-seo', funnel_version: 'organic-v4',
    });
    setEvidenceReloadKey((current) => current + 1);
  };

  const requestEvidenceLogin = () => {
    if (authenticated || !valid) return;
    trackUxEvent('guest_evidence_login_click', {
      ticker,
      stock_code: ticker,
      stock_name: name,
      source: 'stock-seo-evidence',
      return_to: `/stocks/${ticker}`,
      funnel_version: 'organic-v2',
    });
    trackUxEvent('auth_start', {
      ticker,
      stock_code: ticker,
      stock_name: name,
      source: 'stock-seo-evidence',
      intent: 'EVIDENCE_DETAIL',
      return_to: `/stocks/${ticker}`,
      funnel_version: 'organic-v2',
    });
    window.dispatchEvent(new CustomEvent('policy-intel:require-auth', {
      detail: {
        returnTo: `/stocks/${ticker}`,
        panelTab: 'SUMMARY',
        openPanel: false,
        source: 'stock-seo-evidence',
      },
    }));
  };

  useEffect(() => {
    if (!valid || !priceIntentRef.current) return;
    const key = `${ticker}|price|${snap?.tradeDate || ''}`;
    if (funnelViewRef.current.has(key)) return;
    const emit = () => {
      if (funnelViewRef.current.has(key)) return;
      funnelViewRef.current.add(key);
      trackUxEvent('price_view', {
        ticker, stock_code: ticker, source: 'stock-seo', price_available: close !== null,
        trade_date: snap?.tradeDate || null, exposure_mode: 'viewport_35pct', funnel_version: 'organic-v2',
      });
    };
    if (typeof IntersectionObserver === 'undefined') {
      emit();
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.35)) {
        emit();
        observer.disconnect();
      }
    }, { threshold: [0.35] });
    observer.observe(priceIntentRef.current);
    return () => observer.disconnect();
  }, [close, snap?.tradeDate, ticker, valid]);

  useEffect(() => {
    if (!valid || !judgment) return;
    const observers: IntersectionObserver[] = [];
    const observeOnce = (
      node: Element | null,
      key: string,
      eventName: string,
      payload: Record<string, string | number | boolean | null | undefined>,
    ) => {
      if (!node || funnelViewRef.current.has(key)) return;
      const emit = () => {
        if (funnelViewRef.current.has(key)) return;
        funnelViewRef.current.add(key);
        trackUxEvent(eventName, payload);
      };
      if (typeof IntersectionObserver === 'undefined') {
        emit();
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.35)) {
          emit();
          observer.disconnect();
        }
      }, { threshold: [0.35] });
      observer.observe(node);
      observers.push(observer);
    };

    const version = cleanText(judgment.contractVersion);
    observeOnce(valueJudgmentRef.current, `${ticker}|value|${version}`, 'value_judgment_view', {
      ticker, stock_code: ticker, source: 'stock-seo', value_state: judgment.valueState || null,
      market_delivery: judgment.marketDelivery?.state || null, confidence: judgment.confidence || null,
      business_ready: Boolean(judgment.businessEvidence?.ready), exposure_mode: 'viewport_35pct', funnel_version: 'organic-v2',
    });
    observeOnce(reasonCardRef.current, `${ticker}|reason|${version}`, 'judgment_reason_view', {
      ticker, stock_code: ticker, source: 'stock-seo', value_state: judgment.valueState || null, exposure_mode: 'viewport_35pct', funnel_version: 'organic-v2',
    });
    observeOnce(changeCardRef.current, `${ticker}|change|${version}`, 'recent_change_view', {
      ticker, stock_code: ticker, source: 'stock-seo', change_count: visibleValueChanges.length + visibleMarketChanges.length, exposure_mode: 'viewport_35pct', funnel_version: 'organic-v2',
    });
    if (visibleChecks.length > 0) {
      observeOnce(conditionCardRef.current, `${ticker}|conditions|${version}`, 'judgment_condition_view', {
        ticker, stock_code: ticker, source: 'stock-seo', condition_count: visibleChecks.length,
        value_state: judgment.valueState || null, exposure_mode: 'viewport_35pct', funnel_version: 'organic-v2',
      });
      observeOnce(conditionCardRef.current, `${ticker}|change-conditions|${version}`, 'change_condition_view', {
        ticker, stock_code: ticker, source: 'stock-seo', condition_count: visibleChecks.length,
        value_state: judgment.valueState || null, exposure_mode: 'viewport_35pct', funnel_version: 'organic-v2',
      });
    }
    observeOnce(topTrackCtaRef.current, `${ticker}|track-cta-top|${version}`, 'track_cta_view', {
      ticker, stock_code: ticker, source: 'stock-seo', placement: 'value', authenticated,
      value_state: judgment.valueState || null, exposure_mode: 'viewport_35pct', funnel_version: 'organic-v2',
    });
    return () => observers.forEach((observer) => observer.disconnect());
  }, [authenticated, judgment, ticker, valid, visibleChecks.length, visibleMarketChanges.length, visibleValueChanges.length]);

  const title = `${name} 주가·수급·보유 판단·주요 변화 | PolicyIntel`;
  const description = `${name}(${ticker}) 현재 주가와 외국인·기관 수급, 기업가치·투자 논리, 최근 중요한 변화와 판단이 바뀌는 조건을 한 화면에서 확인하세요.`;
  const structuredData = useMemo(() => ({ '@context': 'https://schema.org', '@graph': [
    { '@type': 'WebPage', name: title, url: `https://www.policy-intel.com/stocks/${ticker}`, description, inLanguage: 'ko-KR' },
    { '@type': 'BreadcrumbList', itemListElement: [
      { '@type': 'ListItem', position: 1, name: '폴리시인텔', item: 'https://www.policy-intel.com/' },
      { '@type': 'ListItem', position: 2, name: '국내주식', item: 'https://www.policy-intel.com/stocks' },
      { '@type': 'ListItem', position: 3, name, item: `https://www.policy-intel.com/stocks/${ticker}` },
    ] },
  ] }), [description, name, ticker, title]);

  const track = async () => {
    if (!valid || trackingActive || trackSaving) return;
    if (trackMutationInFlightRef.current === ticker) return;

    trackMutationInFlightRef.current = ticker;
    setTrackSaving(true);

    try {
      const intentId = createTrackIntentId(ticker);
      const extra = {
        ticker,
        stock_code: ticker,
        stock_name: name,
        source: 'stock-seo',
        value_state: judgment?.valueState || null,
        funnel_version: 'organic-v3',
        intent_id: intentId,
      };
      trackUxEvent('stock_track_click', extra);

      // Guest 전환에서는 인증 전에 브라우저 알림 권한을 요구하지 않는다.
      // 먼저 종목 감시 의도를 저장하고, 로그인 후 동일 intent_id로 mutation 결과까지 연결한다.
      if (!authenticated) {
        saveStockTrackPending({
          type: 'WATCHLIST',
          ticker,
          name,
          source: 'stock-seo',
          intentId,
          createdAt: Date.now(),
        });
        trackUxEvent('auth_start', {
          ...extra,
          source: 'stock-seo-track',
          intent: 'WATCHLIST',
          return_to: `/stocks/${ticker}`,
        });
        window.dispatchEvent(new CustomEvent('policy-intel:require-auth', {
          detail: {
            returnTo: `/stocks/${ticker}`,
            panelTab: 'SUMMARY',
            openPanel: false,
            source: 'stock-value-track',
            intent: 'WATCHLIST',
            intentId,
            stockCode: ticker,
            stockName: name,
          },
        }));
        return;
      }

      let mutation: LiveSummaryHoldingMutationResult;
      try {
        mutation = await addLiveSummaryHolding(ticker);
      } catch (cause: any) {
        if (Number(cause?.response?.status) === 409) {
          mutation = {
            ticker,
            active: true,
            added: false,
            action: 'ALREADY_ACTIVE',
          };
        } else {
          setTrackState('추적 저장 확인 필요');
          return;
        }
      }

      const pushStatus = await activateTrackWebPush(ticker, 'stock-seo-track');
      const stateChanged = mutation.action === 'CREATED' || mutation.action === 'RESTORED';
      if (stateChanged) {
        recordTrackedActivation(ticker, retentionIdentity);
      }
      setTrackState(trackStateForMutation(mutation.action, pushStatus));
      trackUxEvent(mutationEventName(mutation.action), {
        ...extra,
        mutation_action: mutation.action,
        state_changed: stateChanged,
        push_status: pushStatus,
      });
    } finally {
      if (trackMutationInFlightRef.current === ticker) {
        trackMutationInFlightRef.current = '';
      }
      setTrackSaving(false);
    }
  };

  const enableTrackNotifications = async () => {
    if (!authenticated || !valid) return;
    await requestTrackPushPermission(ticker, 'stock-seo-post-track');
    const pushStatus = await activateTrackWebPush(ticker, 'stock-seo-post-track');
    setTrackState(`감시 중 · ${trackStateForPush(pushStatus).replace(/^추적 중 · /, '')}`);
    trackUxEvent('push_after_track_result', {
      ticker,
      stock_code: ticker,
      source: 'stock-seo-post-track',
      push_status: pushStatus,
    });
  };

  return <>
    <SEO title={title} description={description} path={`/stocks/${ticker}`} noindex={!valid || notFound} structuredData={structuredData} />
    <main className="stock-value-page"><div className="stock-value-shell">
      <nav className="stock-value-breadcrumb"><Link to="/">PolicyIntel</Link><span>/</span><Link to="/stocks">Stocks</Link><span>/</span><b>{name}</b></nav>

      <section className="stock-value-hero">
        <header className="stock-value-hero__top">
          <div><span className="stock-value-kicker">{ticker}{market ? ` · ${market}` : ''}</span><h1>{name} 주가·수급 | 현재 판단과 변화 조건</h1></div>
        </header>
        <div ref={priceIntentRef} className="stock-value-price-intent">
          <div><small>현재가</small><strong>{fmtPrice(close)}</strong></div>
          <div><small>등락</small><b className={(rate ?? 0) < 0 ? 'is-down' : (rate ?? 0) > 0 ? 'is-up' : ''}>{fmtPct(rate)}</b></div>
          <span>{priceBasisLabel}</span>
          {!authenticated ? <button
            type="button"
            className="stock-value-chart-entry-button"
            aria-expanded={guestChartOpen}
            aria-controls={`stock-seo-guest-chart-${ticker}`}
            onClick={toggleGuestChart}
          >{guestChartOpen ? '차트 닫기' : '주가 차트 보기'}</button> : null}
        </div>

        {judgment && <section className="stock-value-factual-lead" aria-label="검증된 종목 근거">
          <div className="stock-value-factual-lead__heading">
            <strong>확인된 사실</strong>
            <span>판단·예측과 구분해 표시합니다</span>
          </div>
          {visibleObservedEvidence.length ? <div className="stock-value-factual-lead__grid">
            {visibleObservedEvidence.map(fact => <article key={fact.label}>
              <small>{fact.label}</small><strong>{fact.value}</strong><span>{fact.note}</span>
            </article>)}
          </div> : <p>현 단계에서 검증 완료로 표시할 사업·재무·수급 항목이 제한적입니다. 확인되지 않은 근거는 채우지 않습니다.</p>}
        </section>}
        <div className="stock-value-hero__question">{name}의 기업가치 판단과 다음 재검토 조건</div>
        {loading ? <div className="stock-value-loading" role="status">검증된 사업·재무 근거를 확인하고 있습니다.</div>
          : error ? <div className="stock-value-error stock-value-data-feedback" role="alert">
              <p>{error}</p>
              <button type="button" onClick={retryStockEvidence}>다시 조회</button>
            </div>
          : judgment ? <>
            <div ref={valueJudgmentRef} className="stock-value-hero__decision">
              <div className="stock-value-hero__state">
                <small>현재 기업 판단</small>
                <strong className={stateClass(judgment.valueState)}>{limitedState && !limitedWithVerifiedBaseline ? '기업가치 판단 유보' : displayValueHeadline}</strong>
                <p>{judgment.summary || '현재 판단 근거를 확인 중입니다.'}</p>
              </div>
              <div className="stock-value-hero__action">
                <span>{judgment.confidenceLabel || '확인 필요'}</span>
                <button ref={topTrackCtaRef} type="button" onClick={track} disabled={trackButtonDisabled}>{trackButtonLabel}</button>
                <small>{trackSupport}</small>
                {authenticated && (/권한 필요|알림 연결 확인 필요/.test(trackState) || trackState === '이미 감시 중') ? <button className="stock-value-notification-cta" type="button" onClick={enableTrackNotifications}>알림도 받기</button> : null}
              </div>
            </div>
            {limitedWithVerifiedBaseline ? <div className="stock-value-verified-baseline">
              <div><small>기존 확인 상태</small><b>{verifiedState.label}</b>{verifiedState.verifiedAt ? <span>{verifiedState.verifiedAt}</span> : null}</div>
              <div><small>신규 근거 상태</small><b>{newEvidence.label}</b>{newEvidence.summary ? <span>{newEvidence.summary}</span> : null}</div>
            </div> : null}
            {limitedState ? <div className="stock-value-evaluation-limit" role="note">
              <strong>판단이 유보된 이유</strong>
              <span>{financialReady ? '현재 판단에 필요한 일부 추가 근거를 확인하고 있습니다.' : '현재 재무 데이터의 충분성·신선도 검증이 완료되지 않아 가치평가를 확정하지 않습니다.'}</span>
            </div> : <div className="stock-value-state-rail">
              <div><small>기업가치</small><b>{judgment.fundamental?.label || '확인 필요'}</b></div>
              <div><small>투자 논리</small><b>{judgment.thesis?.label || '확인 필요'}</b></div>
              <div className={deliveryClass(judgment.marketDelivery?.state)}><small>시장 반영</small><b>{judgment.marketDelivery?.label || '확인 필요'}</b></div>
            </div>}
          </> : <div className="stock-value-error stock-value-data-feedback" role="status">
              <p>{notFound ? '이 종목의 공개 가능한 판단 데이터를 찾지 못했습니다.' : '현재 확인된 가치 판단이 없습니다. 데이터 없음은 중립 판단을 의미하지 않습니다.'}</p>
              {!notFound && <button type="button" onClick={retryStockEvidence}>다시 조회</button>}
            </div>}
      </section>

      {!authenticated ? <section
        id={`stock-seo-guest-chart-${ticker}`}
        ref={guestChartSectionRef}
        className="stock-value-chart-reveal"
        aria-label={`${name} 주가 차트`}
        hidden={!guestChartOpen}
      >{guestChartOpen ? <>
        <header className="stock-value-chart-reveal__header">
          <div><small>PRICE CHART</small><h2>{name} 주가 차트</h2></div>
          <button type="button" onClick={toggleGuestChart} aria-label="주가 차트 닫기">차트 닫기</button>
        </header>
        <p className="stock-value-chart-reveal__note">일봉 기준 가격 흐름입니다. 기업가치 판단과 구분해 확인하세요.</p>
        <MarketSeriesChart ticker={ticker} view="candles" title={`${name} 주가 차트`} height={420} defaultRange="30D" showDetailPanel={false} showRelatedIssues={false} showDecisionMarkers={false} showEventMarkers={false} />
      </> : null}</section> : null}

      {judgment && <section className="stock-value-priority-grid">
        <article ref={changeCardRef} className="stock-value-change-card">
          <header><span>최근 변화</span><h2>최근 중요한 변화</h2></header>
          {visibleValueChanges.length > 0 ? <div className="stock-value-change-list">{visibleValueChanges.map((x, i) => <div key={x}><em>{String(i + 1).padStart(2, '0')}</em><b>{x}</b></div>)}</div> : <p className="stock-value-empty">최근 판단 변화 기록을 확인하지 못했습니다. 기록 부재는 변화가 없다는 뜻이 아닙니다.</p>}
          {visibleMarketChanges.length > 0 && <div className="stock-value-market-strip"><small>시장 반영 변화</small>{visibleMarketChanges.map((x) => <span key={x}>{x}</span>)}</div>}
          {!authenticated && (valueChanges.length > visibleValueChanges.length || marketChanges.length > visibleMarketChanges.length) ? <p className="stock-value-account-continuity">이전 변화 전체 이력은 종목을 감시하면 계정에 이어집니다.</p> : null}
        </article>
        <article ref={conditionCardRef} className="stock-value-trigger-card">
          <header><span>재검토 조건</span><h2>판단이 바뀌는 조건</h2></header>
          <ol>{visibleChecks.length > 0 ? visibleChecks.map((x) => <li key={x}>{x}</li>) : <li>추가 검증 조건을 확인 중입니다.</li>}</ol>
        </article>
      </section>}

      {judgment && <section className="stock-value-evidence-preview" style={{ marginTop: 12, border: '1px solid #cbd9e8', borderRadius: 16, overflow: 'hidden', background: '#fff', boxShadow: '0 12px 30px rgba(21,42,65,.06)' }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', padding: '14px 16px', background: '#f7faff', borderBottom: '1px solid #dde7f1' }}>
          <div>
            <span style={{ display: 'block', color: '#2d66aa', fontSize: 9, fontWeight: 900, letterSpacing: '.10em' }}>근거 미리보기</span>
            <h2 style={{ margin: '4px 0 0', color: '#20364c', fontSize: 17, letterSpacing: '-.025em' }}>공시 관측 내역과 재무 검증 상태</h2>
          </div>
          {!authenticated ? <span style={{ padding: '5px 8px', borderRadius: 999, background: '#eaf2ff', color: '#285ea8', fontSize: 9, fontWeight: 900 }}>로그인 전 실제 데이터</span> : null}
        </header>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 0, alignItems: 'stretch' }}>
          <article style={{ padding: 16, borderRight: '1px solid #e4eaf0', minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
              <div><small style={{ color: '#8191a1', fontSize: 8.5, fontWeight: 900 }}>최근 공시 근거</small><h3 style={{ margin: '4px 0 0', color: '#25384c', fontSize: 14 }}>최근 이슈·공시</h3></div>
              <span style={{ color: latestDisclosureTitle ? '#087f5b' : '#7d8b99', fontSize: 9, fontWeight: 900 }}>{issueStatusLabel}</span>
            </div>
            {latestDisclosureTitle ? <>
              <p style={{ margin: '12px 0 0', color: '#20364c', fontSize: 12, lineHeight: 1.48, fontWeight: 900 }}>{latestDisclosureTitle}</p>
              <small style={{ display: 'block', marginTop: 4, color: '#98a2b3', fontSize: 9 }}>{latestDisclosureDate || '일자 확인 중'}</small>
            </> : <p style={{ margin: '12px 0 0', color: '#667085', fontSize: 10.5, lineHeight: 1.52 }}>{issueEmptyText}</p>}
            {!authenticated && latestDisclosureTitle ? (
              <div style={{ marginTop: 12, padding: 9, borderRadius: 10, background: '#f7f9fc', border: '1px solid #e5ebf1', color: '#596b7f', fontSize: 9.5, lineHeight: 1.5, fontWeight: 800 }}>
                판단 영향 · {disclosureImpactLabel}
              </div>
            ) : authenticated && disclosures.length > 1 ? <div className="stock-value-change-list">{disclosures.slice(1, 4).map((x: any, i: number) => <div key={`${cleanText(x?.reportName ?? x?.title)}-${i}`}><em>{String(i + 2).padStart(2, '0')}</em><b>{cleanText(x?.reportName ?? x?.report_name ?? x?.title) || '공시'}</b></div>)}</div> : null}
          </article>

          <article style={{ padding: 16, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
              <div><small style={{ color: '#8191a1', fontSize: 8.5, fontWeight: 900 }}>재무 근거</small><h3 style={{ margin: '4px 0 0', color: '#25384c', fontSize: 14 }}>재무 상태</h3></div>
              <span style={{ color: evidenceRefreshPending ? '#a66300' : '#087f5b', fontSize: 9, fontWeight: 900 }}>{evidenceRefreshPending ? '갱신 확인 중' : '핵심 공개'}</span>
            </div>
            <p style={{ margin: '12px 0 0', color: '#20364c', fontSize: 12, lineHeight: 1.48, fontWeight: 900 }}>{judgment.fundamental?.label || '재무 근거 확인 필요'}</p>
            {!authenticated ? <>
              {financialReady && financialReasons.length ? <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
                {financialReasons.slice(0, 3).map((reason, index) => <div key={`${reason}-${index}`} style={{ display: 'grid', gridTemplateColumns: '22px minmax(0,1fr)', gap: 7, padding: 8, borderRadius: 9, background: '#f5f8fb', border: '1px solid #e5ebf1' }}><small style={{ color: '#6c8195', fontSize: 8, fontWeight: 900 }}>{String(index + 1).padStart(2, '0')}</small><b style={{ color: '#40566b', fontSize: 9.5, lineHeight: 1.45 }}>{reason}</b></div>)}
              </div> : <p style={{ margin: '7px 0 0', color: '#667085', fontSize: 10, lineHeight: 1.5 }}>검증 가능한 최신 재무 snapshot을 확인 중입니다. 값이 확인되기 전에는 숫자를 만들어 표시하지 않습니다.</p>}
              <small style={{ display: 'block', marginTop: 7, color: '#8291a3', fontSize: 9 }}>{financialBasisDate ? `재무 기준일 ${financialBasisDate} · ` : ''}상세 추이 · 전체 검증 근거는 로그인 후</small>
            </> : financialReasons.length > 1 ? <div className="stock-value-change-list">{financialReasons.slice(1, 3).map((x, i) => <div key={x}><em>{String(i + 2).padStart(2, '0')}</em><b>{x}</b></div>)}</div> : null}
          </article>
        </div>

        {!authenticated ? <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 10, alignItems: 'center', padding: '12px 16px', background: '#fbfcfe', borderTop: '1px solid #e4eaf0' }}>
          <div><b style={{ color: '#344054', fontSize: 10.5 }}>핵심은 먼저 확인했습니다.</b><span style={{ display: 'block', marginTop: 3, color: '#8291a3', fontSize: 9 }}>전체 근거·이전 변화·재무 상세를 같은 종목에서 이어서 봅니다.</span></div>
          <button type="button" onClick={requestEvidenceLogin} style={{ minHeight: 36, border: 0, borderRadius: 9, background: '#173f65', color: '#fff', padding: '0 14px', fontSize: 10, fontWeight: 900, cursor: 'pointer', whiteSpace: 'nowrap' }}>전체 근거 보기</button>
        </div> : null}
      </section>}

      {judgment && <section ref={reasonCardRef} className="stock-value-evidence">
        <header><div><span>WHY</span><h2>왜 이런 판단인가?</h2></div><small>{business.reportName || '사업 근거 확인 중'}{business.businessDate ? ` · ${business.businessDate}` : ''}</small></header>
        <div className="stock-value-evidence__grid">
          <div><small>사업</small><b>{businessStatus}</b>{(business.industry || business.subIndustry) ? <span>{[business.industry, business.subIndustry].filter(Boolean).join(' · ')}</span> : null}</div>
          <div><small>재무</small><b>{judgment.fundamental?.label || '확인 필요'}</b>{judgment.fundamental?.reasons?.slice(0, 1).map((x) => <span key={x}>{x}</span>)}</div>
          <div><small>투자 논리</small><b>{judgment.thesis?.label || '확인 필요'}</b>{judgment.thesis?.reasons?.slice(0, 1).map((x) => <span key={x}>{x}</span>)}</div>
          <div><small>시장 반영</small><b className={deliveryClass(judgment.marketDelivery?.state)}>{judgment.marketDelivery?.label || '확인 필요'}</b>{judgment.marketDelivery?.reasons?.slice(0, 1).map((x) => <span key={x}>{x}</span>)}</div>
        </div>
        {authenticated ? <>
          <details onToggle={(event) => { if ((event.currentTarget as HTMLDetailsElement).open) trackUxEvent('value_reason_expand', { ticker, stock_code: ticker, source: 'stock-seo' }); }}>
            <summary>재무·판단 근거 상세 보기</summary>
            <div className="stock-value-evidence__detail">{reasons.map((x) => <p key={x}>{x}</p>)}</div>
          </details>
          {business.summary ? <details>
            <summary>사업보고서 근거 상세 보기</summary>
            <div className="stock-value-evidence__detail"><p>{business.summary}</p></div>
          </details> : null}
        </> : <div className="stock-value-evidence__detail" style={{ borderTop: '1px solid #e9eef3', paddingTop: 12 }}>
          <p>비회원에게는 판단을 이해할 수 있는 핵심 근거까지 공개합니다. 전체 재무 근거·사업보고서 상세·이전 변화 이력은 로그인 후 같은 종목에서 이어서 확인합니다.</p>
          <button type="button" onClick={requestEvidenceLogin} style={{ marginTop: 8, border: '1px solid #c7d7e8', borderRadius: 9, background: '#fff', color: '#264f77', padding: '9px 11px', fontSize: 10, fontWeight: 900, cursor: 'pointer' }}>전체 근거 보기</button>
        </div>}
      </section>}

      {judgment ? <ConversionCta ticker={ticker} label={trackButtonLabel} support={trackSupport} authenticated={authenticated} placement="bottom" disabled={trackButtonDisabled} onClick={track} /> : null}

      <details className="stock-value-secondary">
        <summary>
          <span>보조 정보 보기</span>
          <small>{authenticated ? '차트 · 수급 · 업종 · 공시' : '수급 · 업종 · 공시'}</small>
        </summary>
        <div className="stock-value-secondary__body">
      {authenticated ? <section className="stock-value-chart"><header><div><span>PRICE DELIVERY</span><h2>가격은 가치를 어떻게 반영하고 있나</h2></div><small>기업가치 판단과 별도 레이어</small></header><MarketSeriesChart ticker={ticker} view="candles" title={`${name} 주가 차트`} height={420} defaultRange="30D" showDetailPanel={false} showRelatedIssues={false} showDecisionMarkers={false} showEventMarkers={false} /></section> : null}

      <section className="stock-value-market-metrics">
        <div><small>외국인 순매수</small><b>{fmtMoney(foreign)}</b></div><div><small>기관 순매수</small><b>{fmtMoney(institution)}</b></div><div><small>외인+기관</small><b>{fmtMoney(smart)}</b></div><div><small>거래대금</small><b>{fmtMoney(tradingValue)}</b></div>
      </section>

      <div className="stock-value-bottom-grid">
        <section><header><span>INDUSTRY</span><h2>사업·업종 연결</h2></header><div className="stock-value-chips">{Array.isArray(payload?.industryMappings) && payload.industryMappings.length > 0 ? payload.industryMappings.slice(0, 8).map((x: any, i: number) => <span key={`${cleanText(x?.industryCode)}-${i}`}>{cleanText(x?.industryName ?? x?.industryCode)}</span>) : <p>확인된 업종 연결이 없습니다.</p>}</div></section>
        <section><header><span>DISCLOSURES</span><h2>최근 공시</h2></header><div className="stock-value-disclosures">{visibleDisclosures.length > 0 ? visibleDisclosures.map((x: any, i: number) => <div key={i}><b>{cleanText(x?.reportName ?? x?.report_name ?? x?.title) || '공시'}</b><small>{cleanText(x?.receiptDate ?? x?.receipt_date ?? x?.date) || '일자 확인 중'}</small>{!authenticated && i === 0 && disclosureImpactLabel ? <small>판단 영향 · {disclosureImpactLabel}</small> : null}</div>) : <p>{issueEmptyText}</p>}{!authenticated && disclosures.length > visibleDisclosures.length ? <div><b>이전 공시·영향 분석</b><small>로그인 후 이어서 확인</small></div> : null}</div></section>
      </div>
        </div>
      </details>

    </div></main>
  </>;
}
